/**
 * Storefront / driver-portal typography control panel (Admin → Settings →
 * Typography). Lets the admin pick body + heading fonts, base font scale,
 * letter spacing, and which scopes the typography applies to. Includes a
 * live preview card that updates as the admin types — same idea as the
 * favicon Live Preview pane, mirrors the actual storefront card so admins
 * see exactly what customers will see.
 *
 * The chosen settings ship to the backend as the `typography` sub-doc on
 * site_settings; `SettingsContext` reads them on mount and injects CSS
 * variables on <html> + a Google Fonts <link> for the picked families.
 */
import React, { useMemo, useState } from 'react';
import { Smartphone, Truck, ShieldCheck, RotateCcw, Bold, Italic, Monitor, Smartphone as PhoneIcon } from 'lucide-react';
import Rial from './Rial';
import {
  TYPOGRAPHY_FONT_PRESETS,
  TYPOGRAPHY_DEFAULTS,
  TYPOGRAPHY_BASE_SIZES,
  TYPOGRAPHY_SIZE_BOUNDS,
  TYPOGRAPHY_WEIGHT_OPTIONS,
  TYPOGRAPHY_DEVICES,
  resolveTypographyConfig,
  resolveTypographySize,
} from '../lib/typography';

const SCALE_OPTIONS = [
  { value: 0.875, label: 'S',  hint: 'Compact (87.5%)' },
  { value: 1,     label: 'M',  hint: 'Default (100%)' },
  { value: 1.125, label: 'L',  hint: 'Comfortable (112.5%)' },
  { value: 1.25,  label: 'XL', hint: 'Large (125%)' },
];

const SPACING_OPTIONS = [
  { value: -0.5, label: 'Tight' },
  { value: 0,    label: 'Normal' },
  { value: 0.5,  label: 'Wide' },
];

const SCOPE_OPTIONS = [
  { value: 'storefront', label: 'Storefront', icon: Smartphone, hint: 'Customer-facing pages' },
  { value: 'driver',     label: 'Driver portal', icon: Truck, hint: '/driver/*' },
  { value: 'admin',      label: 'Admin panel',  icon: ShieldCheck, hint: 'This page & all admin routes' },
];

// Each row in the per-element size editor. Order matches a natural
// reading flow (hero → section → card → body → label → CTA).
const FIELD_ROWS = [
  { key: 'h1',     label: 'H1 — Page title',     hint: 'Hero headlines, page titles', preview: 'Bulk supplies, business ready.' },
  { key: 'h2',     label: 'H2 — Section title',  hint: 'Section headers like "Recent orders"', preview: 'Featured products' },
  { key: 'h3',     label: 'H3 — Card title',     hint: 'Product card / sidebar titles', preview: 'Ice cream sticks' },
  { key: 'body',   label: 'Body — Paragraphs',   hint: 'Paragraphs, list items, labels', preview: 'Trade pricing, scheduled deliveries, real tax invoices.' },
  { key: 'small',  label: 'Small — Captions',    hint: 'Helper text, captions, badges', preview: 'Includes 5% VAT' },
  { key: 'button', label: 'Button — CTA text',   hint: 'Call-to-action button labels', preview: 'Browse catalogue' },
];

// Product card rows — Category grid + FeaturedProducts. Each row maps
// to a `.product-card-*` CSS class that overrides the Tailwind default
// once the storefront is in typography-scope.
const PRODUCT_FIELD_ROWS = [
  { key: 'product_name',    label: 'Product name',       hint: 'e.g. "RASPBERRY BLAST-STICK"', preview: 'RASPBERRY BLAST-STICK' },
  { key: 'product_size',    label: 'Pack size',          hint: 'e.g. "1X24X50 ML"',           preview: '1X24X50 ML' },
  { key: 'product_min_qty', label: 'Min order qty label',hint: 'e.g. "MIN 1 CTN"',            preview: 'MIN 1 CTN' },
  { key: 'product_price',   label: 'Price',              hint: 'e.g. "OMR 1.008"',            preview: 'OMR 1.008' },
  { key: 'product_uom',     label: 'Price UOM suffix',   hint: 'e.g. "/ ctn"',                preview: '/ ctn' },
];

const CATEGORY_FIELD_ROWS = [
  { key: 'category_tile',   label: 'Category name',  hint: 'Title under each home-page category tile', preview: 'Ice Cream' },
];

const TypographyPanel = ({ form, update }) => {
  const cfg = resolveTypographyConfig(form?.typography);

  // setCfg writes back into the form.typography sub-doc — single source of
  // truth that round-trips through the existing settings PUT endpoint.
  const setCfg = (patch) => update({ typography: { ...cfg, ...patch } });

  // Active device tab — every per-element change writes under this key
  // (`cfg.sizes[device][field]` etc.). The runtime applier then picks
  // the right value via the matching CSS variable / class.
  const [device, setDevice] = useState('desktop');

  /** Update a single per-field size override for the current device. */
  const setFieldSize = (field, value) => {
    setCfg({
      sizes: {
        ...cfg.sizes,
        [device]: { ...cfg.sizes[device], [field]: value },
      },
    });
  };
  const setFieldWeight = (field, value) => {
    setCfg({
      weights: {
        ...cfg.weights,
        [device]: { ...cfg.weights[device], [field]: value },
      },
    });
  };
  const setFieldItalic = (field, value) => {
    setCfg({
      italics: {
        ...cfg.italics,
        [device]: { ...cfg.italics[device], [field]: !!value },
      },
    });
  };

  const toggleScope = (scope) => {
    const scopes = new Set(cfg.apply_to);
    if (scopes.has(scope)) scopes.delete(scope);
    else scopes.add(scope);
    setCfg({ apply_to: Array.from(scopes) });
  };

  // Resolve the actual CSS font stack from the chosen preset key —
  // mirrors what SettingsContext does at runtime so the live preview
  // matches exactly.
  const previewStyle = useMemo(() => {
    const body = TYPOGRAPHY_FONT_PRESETS[cfg.body_font] || TYPOGRAPHY_FONT_PRESETS.system;
    const heading = TYPOGRAPHY_FONT_PRESETS[cfg.heading_font] || TYPOGRAPHY_FONT_PRESETS.system;
    return {
      '--lp-body': body.stack,
      '--lp-heading': heading.stack,
      '--lp-scale': cfg.font_scale,
      '--lp-spacing': `${cfg.letter_spacing}px`,
    };
  }, [cfg]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr,320px] gap-6" data-testid="typography-section">
      {/* Controls */}
      <div className="space-y-5">
        <FontPicker
          label="Body font"
          value={cfg.body_font}
          onChange={(v) => setCfg({ body_font: v })}
          testId="typography-body-font"
        />
        <FontPicker
          label="Heading font"
          value={cfg.heading_font}
          onChange={(v) => setCfg({ heading_font: v })}
          testId="typography-heading-font"
          hint="Pair a display font with a clean body for high-end feel."
        />

        <div>
          <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Base size</label>
          <div className="mt-2 flex gap-2">
            {SCALE_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => setCfg({ font_scale: o.value })}
                title={o.hint}
                className={`flex-1 h-12 rounded-lg border font-bold transition-colors ${
                  cfg.font_scale === o.value
                    ? 'bg-[#0F172A] text-white border-[#0F172A] shadow-sm'
                    : 'bg-white text-neutral-700 border-neutral-300 hover:border-[#0078D4]'
                }`}
                data-testid={`typography-scale-${o.label}`}
                style={{ fontSize: `${o.value}rem` }}
              >
                {o.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-neutral-500 mt-1">Scales every body text and heading proportionally.</p>
        </div>

        <div>
          <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Letter spacing</label>
          <div className="mt-2 flex gap-2">
            {SPACING_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => setCfg({ letter_spacing: o.value })}
                className={`flex-1 h-10 rounded-lg border text-sm font-semibold transition-colors ${
                  cfg.letter_spacing === o.value
                    ? 'bg-[#0F172A] text-white border-[#0F172A] shadow-sm'
                    : 'bg-white text-neutral-700 border-neutral-300 hover:border-[#0078D4]'
                }`}
                data-testid={`typography-spacing-${o.label.toLowerCase()}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {/* Per-element font sizes — fine-grained control on top of the
            global Base size slider. Each row shows a number input + a
            slider, and an "auto" reset button. When a row is on "auto"
            the displayed value derives from base-size × scale. */}
        <div>
          <div className="flex items-baseline justify-between">
            <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">
              Per-element sizes
            </label>
            <span className="text-[10px] text-neutral-400 italic">
              rem · 1rem ≈ 16px
            </span>
          </div>
          <p className="text-[11px] text-neutral-500 mt-1 mb-2">
            Override any element's size individually. Untouched rows scale
            proportionally with the Base size above.
          </p>

          {/* Device tab — every control below writes under `[device]`.
              Mobile rules kick in below 640px on the storefront. */}
          <div
            className="inline-flex rounded-full bg-neutral-100 p-1 mb-3 text-xs font-bold"
            data-testid="typography-device-tabs"
          >
            {TYPOGRAPHY_DEVICES.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDevice(d)}
                className={`px-4 h-8 rounded-full inline-flex items-center gap-1.5 transition-colors ${
                  device === d
                    ? 'bg-white text-[#0F172A] shadow-sm'
                    : 'text-neutral-500 hover:text-[#0F172A]'
                }`}
                data-testid={`typography-device-${d}`}
              >
                {d === 'desktop' ? <Monitor className="w-3.5 h-3.5" /> : <PhoneIcon className="w-3.5 h-3.5" />}
                <span className="capitalize">{d}</span>
              </button>
            ))}
          </div>
          <p className="text-[10px] text-neutral-500 -mt-2 mb-2">
            Showing controls for <strong className="text-[#0F172A] capitalize">{device}</strong>.
            Mobile rules apply at ≤ 640 px viewport width; rows left on "auto" inherit the desktop value.
          </p>
          <div className="space-y-2" data-testid="typography-sizes">
            {FIELD_ROWS.map((row) => (
              <FieldSizeRow
                key={row.key}
                row={row}
                cfg={cfg}
                device={device}
                onChange={(v) => setFieldSize(row.key, v)}
                onWeightChange={(v) => setFieldWeight(row.key, v)}
                onItalicChange={(v) => setFieldItalic(row.key, v)}
              />
            ))}
          </div>
        </div>

        {/* Product card sizes — separate group so the admin sees they're
            dedicated to the storefront catalogue grid. The class names
            this group toggles (`.product-card-*`) only live on the
            Category page + FeaturedProducts. */}
        <div>
          <div className="flex items-baseline justify-between">
            <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">
              Product card sizes
            </label>
            <span className="text-[10px] text-neutral-400 italic">
              Storefront catalogue & home page
            </span>
          </div>
          <p className="text-[11px] text-neutral-500 mt-1 mb-2">
            Fine-tune the typography on each product card — name, pack
            size, min-order label, price, and UOM suffix. Same auto /
            custom semantics as above.
          </p>
          <div className="space-y-2" data-testid="typography-product-sizes">
            {PRODUCT_FIELD_ROWS.map((row) => (
              <FieldSizeRow
                key={row.key}
                row={row}
                cfg={cfg}
                device={device}
                onChange={(v) => setFieldSize(row.key, v)}
                onWeightChange={(v) => setFieldWeight(row.key, v)}
                onItalicChange={(v) => setFieldItalic(row.key, v)}
              />
            ))}
          </div>
        </div>

        {/* Home page categories — controls the 4-up tile grid on the
            main page (`Categories.jsx`). Today exposes the title only. */}
        <div>
          <div className="flex items-baseline justify-between">
            <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">
              Home page divisions
            </label>
            <span className="text-[10px] text-neutral-400 italic">
              Main-page division tiles
            </span>
          </div>
          <p className="text-[11px] text-neutral-500 mt-1 mb-2">
            Style the division title under each tile on the home page.
          </p>
          <div className="space-y-2" data-testid="typography-category-sizes">
            {CATEGORY_FIELD_ROWS.map((row) => (
              <FieldSizeRow
                key={row.key}
                row={row}
                cfg={cfg}
                device={device}
                onChange={(v) => setFieldSize(row.key, v)}
                onWeightChange={(v) => setFieldWeight(row.key, v)}
                onItalicChange={(v) => setFieldItalic(row.key, v)}
              />
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Apply to</label>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
            {SCOPE_OPTIONS.map((s) => {
              const Icon = s.icon;
              const active = cfg.apply_to.includes(s.value);
              return (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => toggleScope(s.value)}
                  className={`p-3 rounded-lg border-2 text-left transition-colors ${
                    active
                      ? 'border-[#0078D4] bg-[#F7FBFF]'
                      : 'border-neutral-200 bg-white hover:border-neutral-300'
                  }`}
                  data-testid={`typography-scope-${s.value}`}
                >
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${active ? 'text-[#0078D4]' : 'text-neutral-500'}`} />
                    <span className={`text-sm font-semibold ${active ? 'text-[#0F172A]' : 'text-neutral-700'}`}>{s.label}</span>
                  </div>
                  <p className="text-[11px] text-neutral-500 mt-1">{s.hint}</p>
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-neutral-500 mt-1">Pick one or more. Most teams leave admin OFF so the editor stays clean.</p>
        </div>
      </div>

      {/* Live preview */}
      <div className="md:sticky md:top-4 md:self-start">
        <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-2">Live preview</div>
        <div
          className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm overflow-hidden"
          style={previewStyle}
          data-testid="typography-preview"
        >
          {/* Mini storefront card mockup */}
          <div className="bg-gradient-to-br from-[#FFF7ED] to-[#FFEDD5] rounded-lg p-4 mb-3 flex items-center gap-3">
            <div className="w-12 h-12 rounded-md bg-white/70" />
            <div className="flex-1 min-w-0">
              <div
                className="text-[10px] uppercase tracking-wider text-neutral-500"
                style={{ fontFamily: 'var(--lp-body)', letterSpacing: 'var(--lp-spacing)' }}
              >
                Glace ice cream
              </div>
              <div
                className="text-base font-bold text-slate-900 truncate"
                style={{ fontFamily: 'var(--lp-heading)', fontSize: `${0.95 * cfg.font_scale}rem` }}
              >
                Vanilla Classic 100ml
              </div>
              <div
                className="text-sm font-semibold text-orange-700"
                style={{ fontFamily: 'var(--lp-body)' }}
              >
                <Rial /> 0.300
              </div>
            </div>
          </div>

          <h1
            className="text-slate-900 font-bold"
            style={{
              fontFamily: 'var(--lp-heading)',
              fontSize: `${resolveTypographySize(cfg, 'h1', device) * 0.65}rem`,
              letterSpacing: `calc(-0.02em + ${cfg.letter_spacing / 16}rem)`,
              lineHeight: 1.1,
            }}
          >
            Bulk supplies,<br />
            <span className="text-orange-500">business ready.</span>
          </h1>
          <p
            className="text-slate-600 mt-2"
            style={{
              fontFamily: 'var(--lp-body)',
              fontSize: `${resolveTypographySize(cfg, 'body', device) * 0.85}rem`,
              letterSpacing: `${cfg.letter_spacing}px`,
            }}
          >
            Source FMCG essentials from one trusted Omani partner — trade pricing,
            scheduled deliveries, real tax invoices.
          </p>
          <button
            className="mt-3 px-4 h-9 rounded-full bg-[#0F172A] text-white font-semibold"
            style={{
              fontFamily: 'var(--lp-body)',
              fontSize: `${resolveTypographySize(cfg, 'button', device)}rem`,
              letterSpacing: `${cfg.letter_spacing}px`,
            }}
          >
            Browse catalogue
          </button>
        </div>
        <p className="text-[11px] text-neutral-500 mt-2 leading-relaxed">
          Updates live as you change settings. Click <b>Save changes</b> at the
          bottom to apply for every customer.
        </p>
      </div>
    </div>
  );
};

const FieldSizeRow = ({ row, cfg, device, onChange, onWeightChange, onItalicChange }) => {
  const bounds = TYPOGRAPHY_SIZE_BOUNDS[row.key];
  // Read directly from the active device bucket — null means "auto" and
  // falls through to the other device's override (via resolveTypographySize)
  // before the global scale.
  const rawSize = cfg.sizes?.[device]?.[row.key];
  const isAuto = rawSize == null;
  const value = resolveTypographySize(cfg, row.key, device);
  const currentWeight = cfg.weights?.[device]?.[row.key] || null;
  const isItalic = !!cfg.italics?.[device]?.[row.key];
  return (
    <div
      className={`p-3 rounded-lg border ${isAuto && !currentWeight && !isItalic ? 'bg-neutral-50 border-neutral-200' : 'bg-[#F4FAFF] border-[#0078D4]/40'}`}
      data-testid={`typography-size-${row.key}`}
    >
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[#0F172A] truncate">{row.label}</div>
          <div className="text-[11px] text-neutral-500 truncate">{row.hint}</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <input
            type="number"
            min={bounds.min}
            max={bounds.max}
            step={bounds.step}
            value={value.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')}
            onChange={(e) => {
              const n = parseFloat(e.target.value);
              if (Number.isFinite(n)) onChange(Math.max(bounds.min, Math.min(bounds.max, n)));
            }}
            className="w-16 h-8 text-sm font-mono text-right rounded-md border border-neutral-300 px-1.5 focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40"
            data-testid={`typography-size-${row.key}-input`}
          />
          <span className="text-[10px] text-neutral-400">rem</span>
          {!isAuto && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="ml-1 p-1 rounded-md text-neutral-400 hover:text-[#0078D4] hover:bg-white"
              title="Reset size to auto (use Base size)"
              data-testid={`typography-size-${row.key}-reset`}
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      <input
        type="range"
        min={bounds.min}
        max={bounds.max}
        step={bounds.step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-[#0078D4]"
        data-testid={`typography-size-${row.key}-slider`}
      />
      <div className="flex items-center justify-between text-[10px] text-neutral-400 mt-0.5">
        <span>{bounds.min} rem</span>
        <span className={`font-semibold ${isAuto ? 'text-neutral-400' : 'text-[#0078D4]'}`}>
          {isAuto
            ? `auto (${value.toFixed(2)} rem · ${Math.round(value * 16)}px)`
            : `custom · ${Math.round(value * 16)}px`}
        </span>
        <span>{bounds.max} rem</span>
      </div>

      {/* Weight + italic — second row of controls per field. Kept on a
          single line on desktop, wraps on mobile. */}
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-neutral-500">
          <Bold className="w-3 h-3" /> Weight
        </span>
        <select
          value={currentWeight ?? ''}
          onChange={(e) => onWeightChange(e.target.value || null)}
          className="h-7 text-xs rounded-md border border-neutral-300 bg-white px-1.5 focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40"
          data-testid={`typography-weight-${row.key}`}
        >
          {TYPOGRAPHY_WEIGHT_OPTIONS.map((o) => (
            <option key={String(o.value)} value={o.value || ''}>{o.label}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => onItalicChange(!isItalic)}
          className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-md border text-xs font-semibold transition-colors ${
            isItalic
              ? 'bg-[#0078D4] text-white border-[#0078D4]'
              : 'bg-white text-neutral-600 border-neutral-300 hover:border-[#0078D4]'
          }`}
          data-testid={`typography-italic-${row.key}`}
          title="Toggle italic"
        >
          <Italic className="w-3.5 h-3.5" />
          Italic
        </button>
      </div>

      {/* Live mini-preview. We deliberately clamp the preview's font
          size to a legible range (1rem–1.75rem) regardless of the
          field's actual size, so weight + italic differences are
          immediately visible even on tiny-by-default fields like
          "Min order qty label" (0.625 rem). The "actual size" badge
          on the slider scale above still communicates the real size. */}
      <div
        className="mt-2 px-3 py-2 rounded-md bg-white border border-neutral-200 text-[#0F172A] truncate flex items-center justify-between gap-3"
        data-testid={`typography-preview-${row.key}`}
      >
        <span
          style={{
            // Scale toward the legible band: 1rem .. 1.75rem.
            // Tiny fields stay readable; big ones don't blow out the row.
            fontSize: `${Math.max(1, Math.min(value * 0.85, 1.75))}rem`,
            fontWeight: currentWeight || (
              ['h1','h2','h3','product_name','product_price','category_tile'].includes(row.key) ? 700 : 400
            ),
            fontStyle: isItalic ? 'italic' : 'normal',
            lineHeight: 1.15,
          }}
        >
          {row.preview || 'Sample · Aa Bb 123'}
        </span>
        <span className="text-[10px] font-mono text-neutral-400 shrink-0">
          {currentWeight ? `w${currentWeight}` : 'auto'}{isItalic ? ' · italic' : ''}
        </span>
      </div>
    </div>
  );
};

const FontPicker = ({ label, value, onChange, testId, hint }) => (
  <div>
    <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">{label}</label>
    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={testId}>
      {Object.entries(TYPOGRAPHY_FONT_PRESETS).map(([key, preset]) => {
        const active = value === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className={`p-3 rounded-lg border-2 text-left transition-colors ${
              active
                ? 'border-[#0078D4] bg-[#F7FBFF]'
                : 'border-neutral-200 bg-white hover:border-neutral-300'
            }`}
            data-testid={`${testId}-${key}`}
          >
            <div
              className={`text-base font-bold ${active ? 'text-[#0F172A]' : 'text-neutral-700'}`}
              style={{ fontFamily: preset.stack }}
            >
              {preset.label}
            </div>
            <div className="text-[10px] text-neutral-500 mt-0.5" style={{ fontFamily: preset.stack }}>
              Aa Bb 123
            </div>
          </button>
        );
      })}
    </div>
    {hint && <p className="text-[11px] text-neutral-500 mt-1">{hint}</p>}
  </div>
);

export default TypographyPanel;
export { TYPOGRAPHY_DEFAULTS };
